import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Route, Routes, Navigate, useParams, useLocation, useNavigate } from "react-router-dom";
import { isMobile } from 'react-device-detect';
import { getCurrentDate, cleanAllOldGameData, markNewsletterSignupDismissed } from "./util";
import { initGA, logGaPageView, logPageView, disableGA, isAnalyticsAllowed, trackNewsletterPopupDismissed, logEvent } from "./analytics";
import { getGameConfig, GAME_CONFIG } from "./games";
import HomePage from "./components/HomePage";
import Terms from "./components/Terms";
import Privacy from "./components/Privacy";
import NotFound from "./components/NotFound";
import GameRouteWrapper from "./components/GameRouteWrapper";
import Ortografia from "./components/Ortografia";
import Scalinata from "./components/Scalinata";
import Collegamenti from "./components/Collegamenti";
import Memoria from "./components/Memoria";
import Cloze from "./components/Cloze";
import Spettri from "./components/Spettri";
import Serpentoni from "./components/Serpentoni";
import GameLandingPage from "./components/GameLandingPage";
import GameArchivePage from "./components/GameArchivePage";
import ConsentBanner from "./components/ConsentBanner";
import NewsletterLandingPage from "./components/NewsletterLandingPage";
import NewsletterSignupContent from "./components/NewsletterSignupContent";
import AccountChangeNoticeModal from "./components/AccountChangeNoticeModal";
import JoinPage from "./components/JoinPage";
import SignInPage from "./components/SignInPage";
import SignUpPage from "./components/SignUpPage";
import FullScreenOverlay from "./components/FullScreenOverlay";
import JoinContent from "./components/JoinContent";
import useSiteConsentFlow from "./hooks/useSiteConsentFlow";
import globalStyles from "./styles/global.module.css";
import { useAuth, useUser } from '@clerk/clerk-react';
import { useAlertQueueActions } from "./contexts/AlertQueueContext";
import { useUserProfile } from "./contexts/UserProfileContext";
import SnackAlert from "./components/SnackAlert";
import ReauthPrompt from "./components/ReauthPrompt";
import ScrollToTop from "./components/ScrollToTop";
import InstallPage from "./components/InstallPage";
import InstallHelper from "./components/InstallHelper";
import { shouldShowInstallHelperModal, markInstallHelperDismissed, detectEnvironment, isInstallPromptAvailable, getInstallStrategy } from "./utils/installHelper";
import { ThemeColorUpdater } from "./hooks/useThemeColor";
import { CanonicalUpdater } from "./hooks/useCanonical";
import AccountPage from "./components/AccountPage";
import AccountLandingPage from "./components/AccountLandingPage";
import GettonciniLandingPage from "./components/GettonciniLandingPage";
import SerieLandingPage from "./components/SerieLandingPage";
import StellineLandingPage from "./components/StellineLandingPage";
import ChiSiamo from "./components/ChiSiamo";
import Contatti from "./components/Contatti";
import Collaborazioni from "./components/Collaborazioni";
import InsegnantiLandingPage from "./components/InsegnantiLandingPage";
import AIHelperLandingPage from "./components/AIHelperLandingPage";
import MondialiRegistrationPage from "./components/MondialiRegistrationPage";
import MondialiHomePage, { MondialiTabbedLayout } from "./components/MondialiHomePage";
import MondialiRegulations from "./components/MondialiRegulations";
import MondialiHomeRedirect from "./components/MondialiHomeRedirect";
import {
  MondialiWeeklyLeaderboardPage,
  MondialiFinalePage,
  MondialiFinalePreviewPage,
  MondialiRangeLeaderboardPreviewPage,
  MondialiTopPlayerPage,
  MondialiTopPlayerPreviewPage,
} from "./components/MondialiLeaderboardsPage";
import MondialiPremiPage, { MondialiPremiPreviewPage } from "./components/MondialiPremiPage";
import MondialiOpeningCeremonyModal from "./components/MondialiOpeningCeremonyModal";
import { ModalProvider, useGlobalModal } from "./contexts/ModalContext";
import { GameplayAccessProvider } from "./contexts/GameplayAccessContext";
import { GameStartAccountOfferProvider } from "./contexts/GameStartAccountOfferProvider";
import { PuzzleInstancesProvider } from "./contexts/PuzzleInstancesContext";
import { GameStatusesProvider } from "./contexts/GameStatusesContext";
import { AnnouncementProvider } from "./contexts/AnnouncementContext";
import { StreakProvider } from "./contexts/StreakContext";
import { GettonciniProvider } from "./contexts/GettonciniContext";
import { LearnedTermsProvider } from "./contexts/LearnedTermsContext";
import { StellineProvider } from "./contexts/StellineContext";
import { StellineRewardsProvider } from "./contexts/StellineRewardsContext";
import { AIHelperProvider } from "./contexts/AIHelperContext";
import { AdsProvider } from "./contexts/AdsContext";
import AIHelperPanel from "./components/ai-helper/AIHelperPanel";
import AIHelperPrompt from "./components/ai-helper/AIHelperPrompt";
import Footer from "./components/Footer";
import ResourceCelebrationLayer from "./components/ResourceCelebrationLayer";
import MilestoneCoordinator from "./components/MilestoneCoordinator";
import { useMilestones } from "./contexts/MilestoneContext";
import DevResourceCelebrationShortcuts from "./components/DevResourceCelebrationShortcuts";
import ClientUpdateRequiredOverlay from "./components/ClientUpdateRequiredOverlay";
import {
  CLIENT_UPDATE_REQUIRED_EVENT,
  CLIENT_UPDATE_REQUIRED_MESSAGE,
  consumePendingClientUpdateRequired,
} from "./api/client";
import { config } from "./config";
import {
  markAccountChangeNoticeShown,
  shouldOpenAccountChangeNoticeModal,
} from "./utils/accountChangeNotice";
import { useHomepagePromoScheduler } from "./hooks/useHomepagePromoScheduler";

// Check if this is the admin portal build
const isAdminPortal = process.env.REACT_APP_IS_ADMIN_PORTAL === 'true';
const LazyAdminApp = React.lazy(() => import("./components/admin/AdminApp"));

function shouldLoadAdminAppForHostname() {
  if (typeof window === "undefined") return true;

  const hostname = (window.location.hostname || "").toLowerCase();
  const isLocalDevHost =
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]" ||
    /^[0-9.]+$/.test(hostname);

  return hostname.includes("admin") || isLocalDevHost;
}

// Component to conditionally render footer (hide on game play pages)
const ConditionalFooter = () => {
  const location = useLocation();
  const path = location.pathname;
  
  // Check if we're on a game play page (e.g., /ortografia/2025-01-15)
  // Game play pages have format: /:gameName/:date
  const gameIds = Object.values(GAME_CONFIG).map(g => g.pathName);
  const isGamePlayPage = gameIds.some(gameId => {
    const regex = new RegExp(`^/${gameId}/\\d{4}-\\d{2}-\\d{2}$`);
    return regex.test(path);
  });
  
  if (isGamePlayPage) {
    return null;
  }
  
  return <Footer />;
};

// Component mapping for games
const GAME_COMPONENTS = {
  spettri: Spettri,
  serpentoni: Serpentoni,
  cloze: Cloze,
  ortografia: Ortografia,
  scalinata: Scalinata,
  collegamenti: Collegamenti,
  memoria: Memoria,
};

const MONDIALI_OPENING_CEREMONY_PREVIEW_VARIANTS = [
  { is_registered: false },
  {
    is_registered: true,
    username: 'azzurro1985',
    country_alpha2: 'IT',
    country_name_it: 'Italia',
    team_players_count: 8,
  },
  {
    is_registered: true,
    username: 'azzurro1985',
    country_alpha2: 'IT',
    country_name_it: 'Italia',
    team_players_count: 3,
  },
];

const PageTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Generate descriptive title based on path
    let title = 'Homepage';
    
    if (location.pathname !== '/') {
      const pathParts = location.pathname.split('/').filter(Boolean);
      if (pathParts.length > 0) {
        const pathName = pathParts[0];
        // Find game config by pathName
        const gameConfig = Object.values(GAME_CONFIG).find(game => game.pathName === pathName);
        
        if (gameConfig) {
          title = gameConfig.displayName;
        } else {
          // Fallback for unknown games
          title = pathName.charAt(0).toUpperCase() + pathName.slice(1);
        }
      }
    }
    
    logGaPageView(location.pathname, title);
  }, [location]);
  
  return null;
};


const App = () => {
  const currentDate = getCurrentDate();
  const [analyticsConsent, setAnalyticsConsent] = useState(() => isAnalyticsAllowed());
  const previousAnalyticsConsentRef = useRef(analyticsConsent);
  const { isEUUser, showConsentBanner, setShowConsentBanner, consentHidden, consentBusy } = useSiteConsentFlow({
    isAdminPortal, setAnalyticsConsent,
  });
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(false);
  const [showClientUpdateOverlay, setShowClientUpdateOverlay] = useState(false);
  const [clientUpdateMessage, setClientUpdateMessage] = useState(CLIENT_UPDATE_REQUIRED_MESSAGE);
  // Refs to allow triggering modals from outside AppRouteSwitch
  const showMondialiOpeningCeremonyModalRef = useRef(null);
  const showNewsletterModalRef = useRef(null);
  const showInstallHelperModalRef = useRef(null);
  const showAccountChangeNoticeModalRef = useRef(null);
  const mondialiOpeningCeremonyPreviewIndexRef = useRef(0);

  // Signal that React has mounted - used by loading fallback in index.html
  // This prevents the Android PWA black screen issue by letting the HTML know the app is alive
  useEffect(() => {
    if (typeof window.__reactMounted === 'function') {
      window.__reactMounted();
    }
  }, []);

  useEffect(() => {
    if (isAdminPortal || typeof window === 'undefined') {
      return undefined;
    }

    const pendingClientUpdate = consumePendingClientUpdateRequired();
    if (pendingClientUpdate?.message) {
      setClientUpdateMessage(pendingClientUpdate.message);
      setShowClientUpdateOverlay(true);
    }

    const handleClientUpdateRequired = (event) => {
      const message = event?.detail?.message || CLIENT_UPDATE_REQUIRED_MESSAGE;
      setClientUpdateMessage(message);
      setShowClientUpdateOverlay(true);
    };

    window.addEventListener(CLIENT_UPDATE_REQUIRED_EVENT, handleClientUpdateRequired);
    return () => {
      window.removeEventListener(CLIENT_UPDATE_REQUIRED_EVENT, handleClientUpdateRequired);
    };
  }, []);

  useEffect(() => {
    if (isAdminPortal) {
      disableGA();
      previousAnalyticsConsentRef.current = analyticsConsent;
      return;
    }

    const hadAnalyticsConsent = previousAnalyticsConsentRef.current === true;

    if (analyticsConsent) {
      initGA();
      // Ensure first post-consent pageview is captured for the current route
      // (for example /benvenuti/:partner_slug before any redirect happens).
      if (!hadAnalyticsConsent && typeof window !== 'undefined') {
        logPageView(window.location.pathname, document.title);
      }
    } else {
      disableGA();
    }

    previousAnalyticsConsentRef.current = analyticsConsent;
  }, [analyticsConsent]);

  // Clean up old game data once on app mount (runs infrequently for better performance)
  useEffect(() => {
    cleanAllOldGameData();
  }, []);

  // Dev-only keyboard shortcuts to test alerts
  // Ctrl+Alt+G: Test gettoncini resource flight
  // Ctrl+Alt+S: Test streak resource flight
  // Ctrl+Alt+L: Test stelline resource flight
  // Ctrl+Alt+P: Test learned terms resource flight
  // Ctrl+Alt+O: Test Mondiali Opening Ceremony popup (cycles variants)
  // Ctrl+Alt+N: Test Newsletter popup
  // Ctrl+Alt+I: Test Install Helper popup
  // Ctrl+Alt+M: Test account change notice modal
  // Ctrl+Alt+Shift+L: Test stelline resource flight
  // Ctrl+Alt+Shift+P: Test learned terms resource flight
  // Console: window.gdpDevResourceCelebrations.streak()
  // Console: window.gdpDevResourceCelebrations.stelline(50)
  // Console: window.gdpDevResourceCelebrations.learnedTerms(4)
  const { queueAlert } = useAlertQueueActions();
  
  // Create stable callbacks for showing modals
  const showMondialiOpeningCeremonyOverlay = useCallback(() => {
    showMondialiOpeningCeremonyModalRef.current?.();
  }, []);

  const showNewsletterOverlay = useCallback(() => {
    showNewsletterModalRef.current?.();
  }, []);
  
  const showInstallHelperOverlay = useCallback(() => {
    showInstallHelperModalRef.current?.();
  }, []);

  const showAccountChangeNoticeOverlay = useCallback(() => {
    showAccountChangeNoticeModalRef.current?.();
  }, []);
  
  useEffect(() => {
    if (config.ENVIRONMENT !== 'development') return;
    
    const handleKeyPress = (e) => {
      if (e.key === 'g' && e.altKey && e.ctrlKey) {
        queueAlert({
          type: 'gettoncini',
          amount: 500,
          reason: 'Completamento del gioco quotidiano',
          transactionKey: 'game_completion',
          duration: 500000,
          delay: 0
        });
      }
      if (e.key === 'o' && e.altKey && e.ctrlKey) {
        e.preventDefault();
        const previewMondiali =
          MONDIALI_OPENING_CEREMONY_PREVIEW_VARIANTS[mondialiOpeningCeremonyPreviewIndexRef.current]
          || MONDIALI_OPENING_CEREMONY_PREVIEW_VARIANTS[0];
        mondialiOpeningCeremonyPreviewIndexRef.current =
          (mondialiOpeningCeremonyPreviewIndexRef.current + 1)
          % MONDIALI_OPENING_CEREMONY_PREVIEW_VARIANTS.length;
        showMondialiOpeningCeremonyModalRef.current?.(previewMondiali);
      }
      if (e.key === 'n' && e.altKey && e.ctrlKey) {
        e.preventDefault();
        showNewsletterOverlay();
      }
      if (e.key === 'i' && e.altKey && e.ctrlKey) {
        e.preventDefault();
        showInstallHelperOverlay();
      }
      if (e.key === 'm' && e.altKey && e.ctrlKey) {
        e.preventDefault();
        showAccountChangeNoticeOverlay();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [queueAlert, showNewsletterOverlay, showInstallHelperOverlay, showAccountChangeNoticeOverlay]);

  // Generate routes dynamically from game configuration
  const gameRoutes = useMemo(() => {
    const allGames = Object.values(GAME_CONFIG).sort((a, b) => a.order - b.order);
    
    return allGames.map(gameConfig => {
      const GameComponent = GAME_COMPONENTS[gameConfig.id];
      
      if (!GameComponent) {
        console.warn(`No component found for game: ${gameConfig.id}`);
        return null;
      }
      
      return (
        <React.Fragment key={gameConfig.id}>
          <Route path={`/${gameConfig.pathName}/:date`} element={<GameRouteWrapper GameComponent={GameComponent} />} />
          <Route path={`/${gameConfig.pathName}/archivio`} element={<GameArchivePage />} />
          <Route path={`/${gameConfig.pathName}`} element={<GameLandingPage />} />
        </React.Fragment>
      );
    }).filter(Boolean);
  }, [currentDate]);

  const AppRouteSwitch = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const state = location.state || {};
    const backgroundLocation = state.backgroundLocation;
    const { isLoaded: authLoaded, isSignedIn } = useAuth();
    const { isSignedIn: userSignedIn, user } = useUser();
    const isAdmin = Boolean(userSignedIn && user?.publicMetadata?.role === 'admin');
    const {
      createdAt: accountCreatedAt,
      profile,
      isLoading: isProfileLoading,
      isNewsletterSubscribed,
    } = useUserProfile();
    const prevLocationRef = React.useRef(location);
    const { openModal, closeModal, isOpen: isGlobalModalOpen } = useGlobalModal();
    const { hasPendingMilestones } = useMilestones();

    // Redirect deprecated modal query parameters to new routes
    React.useEffect(() => {
      if (location.pathname === '/') {
        const params = new URLSearchParams(location.search);
        const modal = params.get('modal');
        
        if (modal === 'join') {
          const params = new URLSearchParams();
          params.set('source', 'deprecated_join_modal');
          params.set('gate_reason', 'join_page');
          navigate(`/signup?${params.toString()}`, { replace: true });
        } else if (modal === 'newsletter') {
          navigate('/newsletter', { replace: true });
        }
      }
    }, [location.pathname, location.search, navigate]);

    // Track previous location to support redirecting back for gating
    React.useEffect(() => {
      prevLocationRef.current = location;
    }, [location]);

    React.useEffect(() => {
      showMondialiOpeningCeremonyModalRef.current = (overrideMondiali = null) => {
        openModal({
          title: "Ceremonia d'Apertura",
          content: (
            <MondialiOpeningCeremonyModal mondiali={overrideMondiali || profile?.mondiali || null} />
          ),
          celebrationType: 'fireworks',
          analytics: {
            source_surface: 'app_router',
            modal_name: 'mondiali_opening_ceremony',
            trigger: 'homepage_opening_ceremony_prompt',
          },
        });
      };
      return () => {
        showMondialiOpeningCeremonyModalRef.current = null;
      };
    }, [openModal, profile]);

    // Set up newsletter modal function for external callers
    React.useEffect(() => {
      showNewsletterModalRef.current = () => {
        openModal({
          content: (
            <NewsletterSignupContent
              showDismiss={true}
              onDismiss={() => {
                markNewsletterSignupDismissed();
                trackNewsletterPopupDismissed();
                closeModal();
              }}
              onSuccess={closeModal}
              onReturnHome={closeModal}
            />
          ),
          analytics: {
            source_surface: 'app_router',
            modal_name: 'newsletter_signup',
            trigger: 'global_newsletter_prompt',
          },
        });
      };
      return () => {
        showNewsletterModalRef.current = null;
      };
    }, [openModal, closeModal]);

    // Set up install helper modal function for external callers
    React.useEffect(() => {
      showInstallHelperModalRef.current = () => {
        logEvent('install_helper_overlay_shown', {});
        // Get strategy to determine title
        const env = detectEnvironment();
        const promptAvailable = isInstallPromptAvailable();
        const strategy = getInstallStrategy(env, promptAvailable);
        const modalTitle = strategy?.title || 'Installa l\'app';
        
        // Handler for when modal is dismissed (called from any dismissal method: X, overlay, Escape, or programmatic)
        const handleModalDismiss = () => {
          markInstallHelperDismissed();
          logEvent('install_helper_overlay_dismissed', {});
        };
        
        openModal({
          title: modalTitle,
          content: (
            <InstallHelper
              mode="modal"
              onClose={() => {
                // When InstallHelper calls onClose (e.g., after successful install),
                // just call closeModal. The openModal's onClose handler (handleModalDismiss)
                // will be called by closeModal, ensuring dismissal logic runs.
                closeModal();
              }}
              trackEvent={(name, data) => logEvent(`install_${name}`, data)}
              dismissOnInstall={true}
            />
          ),
          onClose: handleModalDismiss,
          analytics: {
            source_surface: 'app_router',
            modal_name: 'install_helper',
            trigger: 'install_helper_prompt',
          },
        });
      };
      return () => {
        showInstallHelperModalRef.current = null;
      };
    }, [openModal, closeModal]);

    const openAccountChangeNoticeModal = React.useCallback(() => {
      openModal({
        title: 'Arrivano le stelline!',
        content: (
          <AccountChangeNoticeModal
            onDismiss={() => {
              closeModal('acknowledge_button');
            }}
            onAccountClick={() => {
              markAccountChangeNoticeShown();
              closeModal('account_link_click');
            }}
          />
        ),
        onClose: markAccountChangeNoticeShown,
        analytics: {
          source_surface: 'app_router',
          modal_name: 'account_resources_change_notice',
          trigger: 'returning_user_first_visit',
        },
      });
    }, [openModal, closeModal]);

    React.useEffect(() => {
      showAccountChangeNoticeModalRef.current = openAccountChangeNoticeModal;
      return () => {
        showAccountChangeNoticeModalRef.current = null;
      };
    }, [openAccountChangeNoticeModal]);

    useHomepagePromoScheduler({
      pathname: location.pathname,
      homepageEntryId: location.key || `${location.pathname}${location.search}`,
      isGlobalModalOpen: isGlobalModalOpen || hasPendingMilestones || consentBusy,
      isProfileLoading,
      mondiali: profile?.mondiali || null,
      isNewsletterSubscribed,
      openMondialiOpeningCeremony: showMondialiOpeningCeremonyOverlay,
      openInstallHelper: showInstallHelperOverlay,
      openNewsletter: showNewsletterOverlay,
    });

    React.useEffect(() => {
      if (!shouldOpenAccountChangeNoticeModal({
        isAdminPortalBuild: isAdminPortal,
        authLoaded,
        isGlobalModalOpen: isGlobalModalOpen || consentBusy,
        accountCreatedAt,
      })) {
        return;
      }

      openAccountChangeNoticeModal();
    }, [accountCreatedAt, authLoaded, isGlobalModalOpen, consentBusy, openAccountChangeNoticeModal]);

    const globalSnackRef = useRef();

    // Show any pending post-signout snackbar using existing SnackAlert infra (global)
    useEffect(() => {
      try {
        const msg = sessionStorage.getItem('snackbar:post-signout');
        if (msg) {
          sessionStorage.removeItem('snackbar:post-signout');
          globalSnackRef.current?.showSnackAlert(msg, 'error', 5000);
        }
      } catch (_) {}
    }, []);

    // Listen for global snackbar events to reuse SnackAlert globally
    useEffect(() => {
      const handler = (e) => {
        try {
          const d = e?.detail || {};
          const message = d.message || String(d) || 'Errore';
          const type = d.type || 'error';
          const duration = typeof d.duration === 'number' ? d.duration : 5000;
          globalSnackRef.current?.showSnackAlert(message, type, duration);
        } catch (_) {}
      };
      window.addEventListener('global-snackbar', handler);
      return () => window.removeEventListener('global-snackbar', handler);
    }, []);

    // Show snackbar from URL params after redirect (e.g., after sign out)
    useEffect(() => {
      try {
        const params = new URLSearchParams(location.search);
        const msg = params.get('snackbar');
        if (msg) {
          const type = params.get('snackbar_type') || 'error';
          const durationStr = params.get('snackbar_duration');
          const duration = durationStr ? parseInt(durationStr, 10) : 5000;
          globalSnackRef.current?.showSnackAlert(msg, type, isNaN(duration) ? 5000 : duration);
          // Clean URL by removing snackbar params
          params.delete('snackbar');
          params.delete('snackbar_type');
          params.delete('snackbar_duration');
          const search = params.toString();
          navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
        }
      } catch (_) {}
    }, [location.search, location.pathname, navigate]);

    return (
      <>
        <SnackAlert ref={globalSnackRef} />
        <PageTracker />
        <ThemeColorUpdater />
        <CanonicalUpdater />
        <ScrollToTop />
        <ReauthPrompt />
        <div className={globalStyles.globalContainer}>
          <Routes location={backgroundLocation || location}>
            {/* Home page route */}
            <Route path="/" element={
              <HomePage 
                analyticsConsent={analyticsConsent} 
                setAnalyticsConsent={setAnalyticsConsent} 
                showWelcomeMessage={showWelcomeMessage}
                setShowWelcomeMessage={setShowWelcomeMessage}
              />
            } />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/newsletter" element={<NewsletterLandingPage />} />
            <Route path="/install" element={<InstallPage />} />
            <Route path="/gettoncini" element={<GettonciniLandingPage />} />
            <Route path="/serie" element={<SerieLandingPage />} />
            <Route path="/stelline" element={<StellineLandingPage />} />
            <Route path="/chi-siamo" element={<ChiSiamo />} />
            <Route path="/contatti" element={<Contatti />} />
            <Route path="/collaborazioni" element={<Collaborazioni />} />
            <Route path="/insegnanti" element={<InsegnantiLandingPage />} />
            <Route path="/assistente-ai" element={<AIHelperLandingPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/registrati" element={<AccountLandingPage />} />
            <Route path="/signin/*" element={<SignInPage />} />
            <Route path="/signup/*" element={<SignUpPage />} />
            <Route path="/percorso-iniziale" element={<Navigate to="/" replace />} />
            <Route path="/mondiali/registrazione" element={<MondialiRegistrationPage />} />
            <Route path="/mondiali/home" element={<MondialiHomeRedirect />} />
            <Route path="/mondiali" element={<MondialiTabbedLayout />}>
              <Route path="rose" element={<MondialiHomePage />} />
              <Route path="premi" element={<MondialiPremiPage />} />
              <Route path="classifica/:weekNumber" element={<MondialiWeeklyLeaderboardPage />} />
              <Route path="top-player" element={<MondialiTopPlayerPage />} />
              <Route path="finale" element={<MondialiFinalePage />} />
            </Route>
            <Route
              path="/mondiali/premi/preview"
              element={
                authLoaded
                  ? (isAdmin ? <MondialiPremiPreviewPage /> : <Navigate to="/mondiali/rose" replace />)
                  : null
              }
            />
            <Route
              path="/mondiali/classifica/preview"
              element={
                authLoaded
                  ? (isAdmin ? <MondialiRangeLeaderboardPreviewPage /> : <Navigate to="/mondiali/rose" replace />)
                  : null
              }
            />
            <Route
              path="/mondiali/top-player/preview"
              element={
                authLoaded
                  ? (isAdmin ? <MondialiTopPlayerPreviewPage /> : <Navigate to="/mondiali/rose" replace />)
                  : null
              }
            />
            <Route
              path="/mondiali/finale/preview"
              element={
                authLoaded
                  ? (isAdmin ? <MondialiFinalePreviewPage /> : <Navigate to="/mondiali/rose" replace />)
                  : null
              }
            />
            <Route path="/mondiali/regolamenti" element={<MondialiRegulations />} />
            <Route path="/benvenuti/:partner_slug" element={
              <HomePage
                analyticsConsent={analyticsConsent}
                setAnalyticsConsent={setAnalyticsConsent}
                showWelcomeMessage={false}
                setShowWelcomeMessage={() => {}}
              />
            } />
            {gameRoutes}
            {/* Catch-all route for 404 pages */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          <ConditionalFooter />
        </div>
      </>
    );
  };

  // Admin portal has a simplified structure - no gettoncini needed for preview mode
  if (isAdminPortal) {
    const shouldLoadAdminApp = shouldLoadAdminAppForHostname();

    return (
      <ModalProvider>
        {shouldLoadAdminApp ? (
          <React.Suspense fallback={null}>
            <LazyAdminApp />
          </React.Suspense>
        ) : (
          <NotFound />
        )}
      </ModalProvider>
    );
  }

  return (
      <StreakProvider>
        <GettonciniProvider>
          <StellineRewardsProvider>
            <StellineProvider>
              <LearnedTermsProvider>
                <AIHelperProvider>
                  <AdsProvider>
                    <AnnouncementProvider>
                      <PuzzleInstancesProvider>
                        <GameStatusesProvider>
                          <ModalProvider>
                            <GameStartAccountOfferProvider>
                              <GameplayAccessProvider>
                                <AppRouteSwitch />
                                {showConsentBanner && (
                                  <div hidden={consentHidden} inert={consentHidden}>
                                    <ConsentBanner
                                      onConsentChange={setAnalyticsConsent}
                                      onClose={() => setShowConsentBanner(false)}
                                    />
                                  </div>
                                )}
                                <AIHelperPrompt />
                                <AIHelperPanel />
                                <ResourceCelebrationLayer />
                                <MilestoneCoordinator isOtherModalOpen={isEUUser === null || consentBusy || showClientUpdateOverlay} />
                                <DevResourceCelebrationShortcuts />
                                <ClientUpdateRequiredOverlay
                                  isOpen={showClientUpdateOverlay}
                                  message={clientUpdateMessage}
                                />
                              </GameplayAccessProvider>
                            </GameStartAccountOfferProvider>
                          </ModalProvider>
                        </GameStatusesProvider>
                      </PuzzleInstancesProvider>
                    </AnnouncementProvider>
                  </AdsProvider>
                </AIHelperProvider>
              </LearnedTermsProvider>
            </StellineProvider>
          </StellineRewardsProvider>
        </GettonciniProvider>
      </StreakProvider>
  );
};

export default App;
